// Progressive disclosure: which pages and hunt panels a trainer sees, by level.
// A locked system shows as a quiet "Lv N" line in the menus (never a pop-up),
// and the hunt log notes the moment it opens. Only Arena, Halls and the Guild
// War are gated on the server too; the rest are hidden until they matter, and
// their pages still open by address.
import { charmUntil } from './auracharm.ts';
import { ARENA } from './arena.ts';
import { HALLS } from './halls.ts';
import { CONFIG } from './config.ts';
import { STAR_ITEM, BOSS_TOKEN } from './items.ts';
import { AREAS, REGIONS } from './grounds.ts';
import { EGG_UNLOCK_LEVEL } from './eggs.ts';
import { TRAIL_LEVEL } from './trail.ts';
import { WB } from './world-boss.ts';
import type { TrainerDTO } from './protocol.ts';

export interface Unlock { id: string; name: string; level: number; where: string }

export const UNLOCKS: Unlock[] = [
  { id: 'boosts', name: 'Boosts', level: 10, where: 'the hunt\'s left panel' },
  { id: 'trail', name: 'The Trail', level: TRAIL_LEVEL, where: 'the Trail page' },
  { id: 'guild', name: 'Guilds', level: 15, where: 'the Guild page' },
  { id: 'worldboss', name: 'World Boss', level: WB.unlockLevel, where: 'the World Boss page' },
  { id: 'bosses', name: 'Bosses', level: 40, where: 'the Bosses page' },
  { id: 'arena', name: 'Arena', level: ARENA.unlockLevel, where: 'the Arena page' },
  { id: 'halls', name: 'Element Halls', level: HALLS.unlockLevel, where: 'the Halls page' },
  { id: 'plan', name: 'Battle Plan', level: ARENA.unlockLevel, where: 'the Battle Plan page' },
  { id: 'shrines', name: 'Shrines', level: CONFIG.shrineShardMinLevel, where: 'the hunt\'s left panel' },
  { id: 'eggs', name: 'Eggs', level: EGG_UNLOCK_LEVEL, where: 'the Shop page' },
  // Later regions open by trainer level: the main thing levels are for.
  ...REGIONS.slice(1).map((r): Unlock => ({ id: `region:${r.id}`, name: `${r.name} (${r.mythology} region)`, level: r.base, where: 'the Map' })),
];
export const UNLOCK: Record<string, Unlock> = Object.fromEntries(UNLOCKS.map((u) => [u.id, u]));

type T = Pick<TrainerDTO, 'level' | 'bag' | 'shrines' | 'boosts' | 'charmed'> & { guild?: unknown };

/** True once a system is open to this trainer: by level, or early when they already hold what it uses. */
export function unlocked(t: T, id: string, now = Date.now()): boolean {
  const u = UNLOCK[id];
  if (!u || t.level >= u.level) return true;
  switch (id) {
    case 'bosses': return Object.values(BOSS_TOKEN).some((k) => (t.bag[k] ?? 0) > 0);
    case 'shrines': return (t.shrines?.length ?? 0) > 0 || (t.bag['shrine-shard'] ?? 0) > 0;
    case 'boosts': return t.boosts.xpUntil > now || t.boosts.coinUntil > now || t.boosts.vipUntil > now || Object.values(t.boosts.paid ?? {}).some((u) => (u ?? 0) > now) || charmUntil(t.boosts, now) > 0 || (t.boosts.catchUntil ?? 0) > now || (t.charmed?.length ?? 0) > 0 || (t.bag['xp-charm'] ?? 0) > 0;
    case 'guild': return !!t.guild;
  }
  return false;
}

/** Refine is shown once the trainer holds a star item (they drop on a region's last hunts) or a creature is refined. */
export function refineUnlocked(t: Pick<TrainerDTO, 'bag'>, refined: boolean): boolean {
  return refined || Object.values(STAR_ITEM).some((k) => (t.bag[k] ?? 0) > 0);
}

/** Hunt areas whose level is passed going from `from` to `to` (each area's hunts open at its level). */
export function areasBetween(from: number, to: number) {
  return AREAS.filter((a) => a.level > from && a.level <= to);
}

/** Systems that open exactly when a trainer goes from `from` to `to`. */
export function unlocksBetween(from: number, to: number): Unlock[] {
  return UNLOCKS.filter((u) => u.level > from && u.level <= to);
}
